import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { TierForm, normalizeTier } from '../components/TierForm';
import {
  ArrowRight, ArrowLeft, Check, CreditCard, Layers, Settings, X,
  Plus, Send, Link, User, Phone, Tag, Sparkles, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYMENT_METHODS = ['Venmo', 'Zelle', 'PayPal', 'Cash App', 'Other'];

const STEPS = [
  { key: 'payment', label: 'Payment Methods', icon: CreditCard },
  { key: 'entry', label: 'Entry Tiers', icon: Layers },
  { key: 'addons', label: 'Add-Ons', icon: Sparkles },
  { key: 'settings', label: 'Event Settings', icon: Settings },
];

function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="step-indicator">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={`step-${i}`}
          className={`h-1 rounded-full transition-all duration-300 ${
            i <= current ? 'bg-[#C6A66B] flex-[2]' : 'bg-[#1A1A1A] flex-1'
          }`}
        />
      ))}
    </div>
  );
}

function EmptyHint({ text }) {
  return (
    <div className="text-center py-6 border border-dashed border-[#1A1A1A] rounded-sm">
      <p className="text-sm text-[#777]">{text}</p>
    </div>
  );
}

export default function SetupDefaults() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);

  // Payment methods
  const [methods, setMethods] = useState([]);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [newMethod, setNewMethod] = useState({ method: '', payment_method_type: 'link', url: '', recipient_name: '', email_or_phone: '', suggested_memo: '', instructions: '' });

  // Pass tiers (entry + addon)
  const [entryTiers, setEntryTiers] = useState([]);
  const [addonTiers, setAddonTiers] = useState([]);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showAddAddon, setShowAddAddon] = useState(false);
  const [newEntry, setNewEntry] = useState({ tier_name: '', price: 0, quantity_available: 0, attendees_per_pass: 1, description: '', is_comp: false, type: 'entry', allow_quantity_selection: true, min_quantity: '', max_quantity: '' });
  const [newAddon, setNewAddon] = useState({ tier_name: '', price: 0, quantity_available: 0, description: '', type: 'addon', allow_quantity_selection: true, min_quantity: '', max_quantity: '' });

  // Host defaults
  const [defaults, setDefaults] = useState({
    release_method: 'automatic',
    notification_schedule: 'instant',
    staff_access_pre_start_minutes: null,
    staff_access_post_end_minutes: null,
  });

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [pmRes, tierRes, defRes] = await Promise.all([
        axios.get(`${API}/users/payment-methods`, { headers }),
        axios.get(`${API}/users/pass-tiers`, { headers }),
        axios.get(`${API}/users/host-defaults`, { headers }),
      ]);
      setMethods(pmRes.data);
      setEntryTiers(tierRes.data.filter(t => t.type === 'entry'));
      setAddonTiers(tierRes.data.filter(t => t.type === 'addon'));
      setDefaults(defRes.data);
    } catch (err) {
      console.error('Failed to load setup defaults:', err);
    }
    finally { setLoading(false); }
  };

  // --- Payment method handlers ---
  const savePaymentMethod = async () => {
    if (!newMethod.method) { toast.error('Select a payment method'); return; }
    if (newMethod.payment_method_type === 'link' && !newMethod.url) { toast.error('Enter a payment link URL'); return; }
    if (newMethod.payment_method_type === 'direct' && !newMethod.recipient_name) { toast.error('Enter recipient name'); return; }
    try {
      const payload = { ...newMethod, id: `pm_${Date.now()}`, is_default: methods.length === 0 };
      await axios.post(`${API}/users/payment-methods`, payload, { headers });
      setMethods(prev => [...prev, payload]);
      setNewMethod({ method: '', payment_method_type: 'link', url: '', recipient_name: '', email_or_phone: '', suggested_memo: '', instructions: '' });
      setShowAddMethod(false);
      toast.success('Payment method saved');
    } catch { toast.error('Failed to save'); }
  };

  const deleteMethod = async (id) => {
    try {
      await axios.delete(`${API}/users/payment-methods/${id}`, { headers });
      setMethods(prev => prev.filter(m => m.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed to remove'); }
  };

  // --- Tier handlers (shared logic) ---
  const saveTier = async (tierData, type) => {
    if (!tierData.tier_name) { toast.error('Enter a name'); return; }
    try {
      const payload = {
        ...normalizeTier(tierData),
        id: `tier_${Date.now()}`,
        type,
      };
      await axios.post(`${API}/users/pass-tiers`, payload, { headers });
      if (type === 'entry') {
        setEntryTiers(prev => [...prev, payload]);
        setNewEntry({ tier_name: '', price: 0, quantity_available: 0, attendees_per_pass: 1, description: '', is_comp: false, type: 'entry', allow_quantity_selection: true, min_quantity: '', max_quantity: '' });
        setShowAddEntry(false);
      } else {
        setAddonTiers(prev => [...prev, payload]);
        setNewAddon({ tier_name: '', price: 0, quantity_available: 0, description: '', type: 'addon', allow_quantity_selection: true, min_quantity: '', max_quantity: '' });
        setShowAddAddon(false);
      }
      toast.success(`${type === 'entry' ? 'Entry tier' : 'Add-on'} saved`);
    } catch { toast.error('Failed to save'); }
  };

  const deleteTier = async (id, type) => {
    try {
      await axios.delete(`${API}/users/pass-tiers/${id}`, { headers });
      if (type === 'entry') setEntryTiers(prev => prev.filter(t => t.id !== id));
      else setAddonTiers(prev => prev.filter(t => t.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed to remove'); }
  };

  // --- Host defaults ---
  const saveDefaults = async () => {
    try {
      await axios.put(`${API}/users/host-defaults`, {
        release_method: defaults.release_method,
        convenience_fee: parseFloat(defaults.convenience_fee) || 0,
        notification_schedule: defaults.notification_schedule,
        staff_access_pre_start_minutes: defaults.staff_access_pre_start_minutes,
        staff_access_post_end_minutes: defaults.staff_access_post_end_minutes,
      }, { headers });
      toast.success('Event defaults saved');
    } catch { toast.error('Failed to save defaults'); }
  };

  const next = () => {
    if (step === 3) saveDefaults();
    if (step < 4) setStep(step + 1);
  };
  const back = () => { if (step > 0) setStep(step - 1); };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-10 h-10 border-3 border-[#C6A66B] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Step 5 = done screen
  const isDone = step === 4;

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        {!isDone && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => navigate('/dashboard')} className="text-xs text-[#777] hover:text-[#B9B1A6] transition-colors" data-testid="exit-setup">
                Exit setup
              </button>
              <div className="text-xs text-[#777]">Step {step + 1} of 4</div>
            </div>
            <StepIndicator current={step} total={4} />
            <div className="mt-6 flex items-center gap-3">
              {React.createElement(STEPS[step].icon, { className: 'w-5 h-5 text-[#C6A66B]' })}
              <h1 className="text-xl font-bold text-[#F3EEE7] tracking-tight" data-testid="step-title">{STEPS[step].label}</h1>
            </div>
          </div>
        )}

        {/* ════ Step 1: Payment Methods ════ */}
        {step === 0 && (
          <div data-testid="setup-step-payment">
            <p className="text-sm text-[#B9B1A6] mb-6">
              Add your preferred payment methods so they're ready for every event you create.
            </p>

            {methods.length > 0 ? (
              <div className="space-y-2 mb-4">
                {methods.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid={`saved-method-${m.id}`}>
                    <div className="flex items-center gap-3">
                      {m.payment_method_type === 'direct' ? <Send className="w-4 h-4 text-[#C6A66B]" /> : <Link className="w-4 h-4 text-[#C6A66B]" />}
                      <div>
                        <div className="text-sm font-medium text-[#F3EEE7]">{m.method}</div>
                        <div className="text-xs text-[#777]">{m.payment_method_type === 'direct' ? m.recipient_name : m.url}</div>
                      </div>
                    </div>
                    <button onClick={() => deleteMethod(m.id)} className="text-[#777] hover:text-[#C44545] transition-colors" data-testid={`delete-method-${m.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyHint text="Adding a payment method helps guests pay you directly. You can always add more later." />
            )}

            {showAddMethod ? (
              <div className="mt-4 p-4 bg-[#0A0A0A] border border-[#C6A66B]/20 rounded-sm space-y-3" data-testid="add-method-form">
                <div>
                  <Label className="text-[#F3EEE7]">Method</Label>
                  <select value={newMethod.method} onChange={e => setNewMethod(p => ({ ...p, method: e.target.value }))} className="w-full mt-1 px-3 py-2 rounded-sm bg-[#050505] border border-[#1A1A1A] text-[#F3EEE7]" data-testid="method-select">
                    <option value="">Select...</option>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[#F3EEE7]">Type</Label>
                  <div className="flex gap-2 mt-1">
                    {[{ val: 'link', label: 'Link-Based' }, { val: 'direct', label: 'Direct Transfer' }].map(t => (
                      <button key={t.val} type="button" onClick={() => setNewMethod(p => ({ ...p, payment_method_type: t.val }))}
                        className={`flex-1 py-2 rounded-sm text-xs font-semibold transition-all ${newMethod.payment_method_type === t.val ? 'bg-[#C6A66B]/15 border border-[#C6A66B]/40 text-[#C6A66B]' : 'bg-[#0A0A0A] border border-[#1A1A1A] text-[#777]'}`}
                        data-testid={`type-${t.val}`}>{t.label}</button>
                    ))}
                  </div>
                </div>
                {newMethod.payment_method_type === 'link' ? (
                  <div><Label className="text-[#F3EEE7]">Payment URL</Label><Input value={newMethod.url} onChange={e => setNewMethod(p => ({ ...p, url: e.target.value }))} placeholder="https://venmo.com/yourname" className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7]" data-testid="method-url" /></div>
                ) : (
                  <>
                    <div><Label className="text-[#F3EEE7]">Recipient Name</Label><Input value={newMethod.recipient_name} onChange={e => setNewMethod(p => ({ ...p, recipient_name: e.target.value }))} className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7]" data-testid="method-recipient" /></div>
                    <div><Label className="text-[#F3EEE7]">Email or Phone</Label><Input value={newMethod.email_or_phone} onChange={e => setNewMethod(p => ({ ...p, email_or_phone: e.target.value }))} className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7]" data-testid="method-contact" /></div>
                    <div><Label className="text-[#F3EEE7]">Suggested Memo</Label><Input value={newMethod.suggested_memo} onChange={e => setNewMethod(p => ({ ...p, suggested_memo: e.target.value }))} placeholder="e.g., Event Name" className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7]" data-testid="method-memo" /></div>
                  </>
                )}
                <div><Label className="text-[#F3EEE7]">Instructions (optional)</Label><Textarea value={newMethod.instructions} onChange={e => setNewMethod(p => ({ ...p, instructions: e.target.value }))} rows={2} className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7]" data-testid="method-instructions" /></div>
                <div className="flex gap-2 pt-1">
                  <Button type="button" onClick={() => setShowAddMethod(false)} variant="outline" className="flex-1 border-[#1A1A1A] text-[#777]">Cancel</Button>
                  <Button type="button" onClick={savePaymentMethod} className="flex-1 bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] font-bold" data-testid="save-method-button">Save Method</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddMethod(true)} className="mt-4 w-full flex items-center justify-center gap-2 py-3 border border-dashed border-[#C6A66B]/30 rounded-sm text-sm text-[#C6A66B] hover:bg-[#C6A66B]/5 transition-colors" data-testid="add-method-trigger">
                <Plus className="w-4 h-4" /> Add Payment Method
              </button>
            )}
          </div>
        )}

        {/* ════ Step 2: Entry Tiers ════ */}
        {step === 1 && (
          <div data-testid="setup-step-entry">
            <p className="text-sm text-[#B9B1A6] mb-6">
              Create reusable entry tiers you use for most events. These load automatically when you create a new event.
            </p>

            {entryTiers.length > 0 ? (
              <div className="space-y-2 mb-4">
                {entryTiers.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid={`saved-entry-${t.id}`}>
                    <div>
                      <div className="text-sm font-medium text-[#F3EEE7]">{t.tier_name}</div>
                      <div className="text-xs text-[#777]">{t.is_comp ? 'Free' : `$${t.price}`} · {t.attendees_per_pass > 1 ? `${t.attendees_per_pass} guests/pass` : 'Single'}</div>
                    </div>
                    <button onClick={() => deleteTier(t.id, 'entry')} className="text-[#777] hover:text-[#C44545] transition-colors" data-testid={`delete-entry-${t.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyHint text="No saved entry tiers yet. Add the tiers you reuse across events — like General, VIP, or Couples." />
            )}

            {showAddEntry ? (
              <div className="mt-4 p-4 bg-[#0A0A0A] border border-[#C6A66B]/20 rounded-sm" data-testid="add-entry-form">
                <TierForm tier={newEntry} onChange={setNewEntry} showTypeSelector={false} compact />
                <div className="flex gap-2 pt-3 mt-3 border-t border-[#1A1A1A]">
                  <Button type="button" onClick={() => setShowAddEntry(false)} variant="outline" className="flex-1 border-[#1A1A1A] text-[#777]">Cancel</Button>
                  <Button type="button" onClick={() => saveTier(newEntry, 'entry')} className="flex-1 bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] font-bold" data-testid="save-entry-button">Save Tier</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddEntry(true)} className="mt-4 w-full flex items-center justify-center gap-2 py-3 border border-dashed border-[#C6A66B]/30 rounded-sm text-sm text-[#C6A66B] hover:bg-[#C6A66B]/5 transition-colors" data-testid="add-entry-trigger">
                <Plus className="w-4 h-4" /> Add Entry Tier
              </button>
            )}
          </div>
        )}

        {/* ════ Step 3: Add-On Templates ════ */}
        {step === 2 && (
          <div data-testid="setup-step-addons">
            <p className="text-sm text-[#B9B1A6] mb-6">
              Save your VIP extras and upgrades so they're ready to attach to any event.
            </p>

            {addonTiers.length > 0 ? (
              <div className="space-y-2 mb-4">
                {addonTiers.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#D4AF37]/15 rounded-sm" data-testid={`saved-addon-${t.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">Add-On</span>
                        <span className="text-sm font-medium text-[#F3EEE7]">{t.tier_name}</span>
                      </div>
                      <div className="text-xs text-[#777] mt-0.5">${t.price}</div>
                    </div>
                    <button onClick={() => deleteTier(t.id, 'addon')} className="text-[#777] hover:text-[#C44545] transition-colors" data-testid={`delete-addon-${t.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyHint text="No add-ons saved yet. Think: table upgrades, bar access, merch bundles, photo booths." />
            )}

            {showAddAddon ? (
              <div className="mt-4 p-4 bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-sm" data-testid="add-addon-form">
                <TierForm tier={newAddon} onChange={setNewAddon} showTypeSelector={false} compact />
                <div className="flex gap-2 pt-3 mt-3 border-t border-[#1A1A1A]">
                  <Button type="button" onClick={() => setShowAddAddon(false)} variant="outline" className="flex-1 border-[#1A1A1A] text-[#777]">Cancel</Button>
                  <Button type="button" onClick={() => saveTier(newAddon, 'addon')} className="flex-1 bg-[#D4AF37] hover:bg-[#C6A66B] text-[#050505] font-bold" data-testid="save-addon-button">Save Add-On</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddAddon(true)} className="mt-4 w-full flex items-center justify-center gap-2 py-3 border border-dashed border-[#D4AF37]/30 rounded-sm text-sm text-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors" data-testid="add-addon-trigger">
                <Plus className="w-4 h-4" /> Add VIP Extra / Upgrade
              </button>
            )}
          </div>
        )}

        {/* ════ Step 4: Event Settings ════ */}
        {step === 3 && (
          <div data-testid="setup-step-settings">
            <p className="text-sm text-[#B9B1A6] mb-6">
              Set your preferred defaults. These will pre-fill when you create a new event.
            </p>

            <div className="space-y-5">
              <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm">
                <Label className="text-[#F3EEE7] font-medium">Access Release Method</Label>
                <p className="text-xs text-[#777] mt-0.5 mb-2">How guests receive their passes after purchasing.</p>
                <div className="flex gap-2">
                  {[{ val: 'automatic', label: 'Instant Release', desc: 'Guests get passes immediately' }, { val: 'manual', label: 'Manual Approval', desc: 'You review each request first' }].map(opt => (
                    <button key={opt.val} type="button" onClick={() => setDefaults(p => ({ ...p, release_method: opt.val }))}
                      className={`flex-1 p-3 rounded-sm text-left transition-all ${defaults.release_method === opt.val ? 'bg-[#C6A66B]/10 border border-[#C6A66B]/40' : 'bg-[#050505] border border-[#1A1A1A]'}`}
                      data-testid={`release-${opt.val}`}>
                      <div className={`text-sm font-medium ${defaults.release_method === opt.val ? 'text-[#C6A66B]' : 'text-[#F3EEE7]'}`}>{opt.label}</div>
                      <div className="text-xs text-[#777] mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm">
                <Label className="text-[#F3EEE7] font-medium">Notification Schedule</Label>
                <p className="text-xs text-[#777] mt-0.5 mb-2">How often you're notified about new ticket purchases.</p>
                <select value={defaults.notification_schedule} onChange={e => setDefaults(p => ({ ...p, notification_schedule: e.target.value }))} className="w-full px-3 py-2 rounded-sm bg-[#050505] border border-[#1A1A1A] text-[#F3EEE7]" data-testid="default-notification">
                  <option value="instant">Instant</option>
                  <option value="daily_1x">Daily</option>
                  <option value="daily_2x">Twice Daily</option>
                  <option value="daily_3x">Three Times Daily</option>
                </select>
              </div>

              <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="default-staff-window-card">
                <Label className="text-[#F3EEE7] font-medium">Default Staff Access Window</Label>
                <p className="text-xs text-[#777] mt-0.5 mb-3">When Quick Event Setup is on, new events will pre-fill the staff access window using these offsets relative to your event start/end. You can always override per event.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] text-[#AFAFAF] mb-1.5">Opens before start</div>
                    <div className="flex gap-1 flex-wrap" data-testid="default-pre-start-presets">
                      {[120, 60, 30, 15].map(mins => (
                        <button
                          key={`pre-${mins}`}
                          type="button"
                          onClick={() => setDefaults(p => ({ ...p, staff_access_pre_start_minutes: mins }))}
                          className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                            defaults.staff_access_pre_start_minutes === mins
                              ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:border-[#D4AF37]/40'
                          }`}
                          data-testid={`default-pre-start-${mins}`}
                        >
                          {mins === 120 ? '−2h' : mins === 60 ? '−1h' : `−${mins}m`}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setDefaults(p => ({ ...p, staff_access_pre_start_minutes: null }))}
                        className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                          defaults.staff_access_pre_start_minutes == null
                            ? 'border-[#777] text-[#777] bg-[#1A1A1A]/40'
                            : 'border-[#1A1A1A] text-[#555] hover:text-[#AFAFAF]'
                        }`}
                        data-testid="default-pre-start-clear"
                      >
                        Off
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] text-[#AFAFAF] mb-1.5">Closes after end</div>
                    <div className="flex gap-1 flex-wrap" data-testid="default-post-end-presets">
                      {[15, 30, 60, 120].map(mins => (
                        <button
                          key={`post-${mins}`}
                          type="button"
                          onClick={() => setDefaults(p => ({ ...p, staff_access_post_end_minutes: mins }))}
                          className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                            defaults.staff_access_post_end_minutes === mins
                              ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:border-[#D4AF37]/40'
                          }`}
                          data-testid={`default-post-end-${mins}`}
                        >
                          {mins === 120 ? '+2h' : mins === 60 ? '+1h' : `+${mins}m`}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setDefaults(p => ({ ...p, staff_access_post_end_minutes: null }))}
                        className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                          defaults.staff_access_post_end_minutes == null
                            ? 'border-[#777] text-[#777] bg-[#1A1A1A]/40'
                            : 'border-[#1A1A1A] text-[#555] hover:text-[#AFAFAF]'
                        }`}
                        data-testid="default-post-end-clear"
                      >
                        Off
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════ Done Screen ════ */}
        {isDone && (
          <div className="text-center py-12" data-testid="setup-complete">
            <div className="w-16 h-16 rounded-full bg-[#C6A66B]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C6A66B]" />
            </div>
            <h1 className="text-2xl font-black text-[#F3EEE7] tracking-tight mb-2">You're all set.</h1>
            <p className="text-sm text-[#B9B1A6] mb-2">Your defaults are saved.</p>
            <p className="text-xs text-[#777] mb-8 max-w-xs mx-auto">Next time you create an event, toggle "Quick Event Setup" to load everything in one tap.</p>

            <div className="space-y-3">
              {/* Summary */}
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {methods.length > 0 && <span className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm px-3 py-1.5 text-[#B9B1A6]">{methods.length} payment method{methods.length !== 1 && 's'}</span>}
                {entryTiers.length > 0 && <span className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm px-3 py-1.5 text-[#B9B1A6]">{entryTiers.length} entry tier{entryTiers.length !== 1 && 's'}</span>}
                {addonTiers.length > 0 && <span className="text-xs bg-[#0A0A0A] border border-[#D4AF37]/15 rounded-sm px-3 py-1.5 text-[#D4AF37]">{addonTiers.length} add-on{addonTiers.length !== 1 && 's'}</span>}
              </div>

              <button
                onClick={() => navigate('/events/create')}
                className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] px-6 py-3.5 rounded-sm font-bold text-sm transition-opacity flex items-center justify-center gap-2"
                data-testid="setup-create-event-button"
              >
                Create My First Event <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full border border-[#1A1A1A] hover:bg-[#0A0A0A] text-[#B9B1A6] px-6 py-3 rounded-sm text-sm transition-colors"
                data-testid="setup-go-dashboard"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* ════ Navigation ════ */}
        {!isDone && (
          <div className="mt-8 flex gap-3" data-testid="step-navigation">
            {step > 0 && (
              <Button type="button" onClick={back} variant="outline" className="border-[#1A1A1A] text-[#777] hover:text-[#F3EEE7]" data-testid="step-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            )}
            <button
              onClick={next}
              className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] px-6 py-3 rounded-sm font-bold text-sm transition-opacity flex items-center justify-center gap-2"
              data-testid="step-next"
            >
              {step === 3 ? 'Save & Finish' : 'Continue'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Skip hint */}
        {!isDone && (
          <p className="text-center text-xs text-[#555] mt-4">
            {step < 3 ? 'You can skip this step and come back later.' : 'These defaults load automatically in event creation.'}
          </p>
        )}
      </div>
    </div>
  );
}
